// Port of new arty's components/arty/community/use-ask-submit.ts (arty repo).
// Client divergences: the success toast is client's `notify` (the one sonner host) rather
// than a root of its own, every ask fires `arty_community_ask_posted` (new arty sends a
// poll to a PostHog-only poll event), and the input is built by `~/lib/community-ask`.
import { useState } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { notify } from '~/components/arty/actions/ArtyActionsProvider';
import { useAnalytics } from '~/context/analytics';
import { COPY_IMAGE_TO_SOCIAL, CREATE_COMMUNITY_POST_MUTATION } from '~/graphql/mutations';
import type { CommunityKind } from '~/lib/arty-actions/catalog';
import { postInput, type AskDraft } from '~/lib/community-ask';

/**
 * Social copies made this session that no post has claimed yet, keyed by piece and ask.
 *
 * `copyImageToSocial` succeeding and `createCommunityPost` failing is the one partial
 * failure here. A retry from scratch would copy the image again and orphan the first,
 * so the id is kept and reused. It is never deleted on failure either: the post may
 * have committed and only its response been lost. Module scope because the dialog
 * unmounts on close, and the retry this exists for can come after a reopen.
 */
const unclaimedCopies = new Map<string, string>();

/** The server's own wording ("Critique intent must be...") beats a house error. */
function serverMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApolloError)) {
    return fallback;
  }
  return error.graphQLErrors[0]?.message?.trim() || fallback;
}

/**
 * Posting an ask is two mutations, in this order: the vault image is copied into a
 * community social image, then the post is created against that id. arthelper will not
 * take a vault image id on a post, and the copy lands `processed`, so nothing is polled.
 */
export function useAskSubmit({
  kind,
  imageId,
  communityId,
  onPosted,
}: {
  kind: CommunityKind;
  imageId: string;
  communityId: string | null;
  onPosted: () => void;
}) {
  const { t } = useTranslation('arty-card');
  const { amplitude } = useAnalytics();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [copyImage] = useMutation(COPY_IMAGE_TO_SOCIAL);
  const [createPost] = useMutation(CREATE_COMMUNITY_POST_MUTATION);
  const failed = t('community-ask.failed');
  const key = `${imageId}:${kind}`;

  const socialCopy = async (): Promise<string | null> => {
    const kept = unclaimedCopies.get(key);
    if (kept) {
      return kept;
    }
    const copied = await copyImage({ variables: { input: { sourceImageId: imageId, contextType: 'community' } } });
    const id = copied.data?.copyImageToSocial?.id ?? null;
    if (id) {
      unclaimedCopies.set(key, id);
    }
    return id;
  };

  const post = async (draft: AskDraft) => {
    const socialImageId = await socialCopy();
    if (!socialImageId || !communityId) {
      setError(failed);
      return;
    }
    const created = await createPost({ variables: { input: postInput(kind, communityId, socialImageId, draft) } });
    const postId = created.data?.createCommunityPost?.id;
    if (!postId) {
      setError(failed);
      return;
    }
    // The copy belongs to a post now; the next ask starts a fresh one.
    unclaimedCopies.delete(key);
    amplitude.track('arty_community_ask_posted', { kind, post_id: postId, image_id: imageId });
    notify(t(`community-ask.kinds.${kind}.posted`), 'success');
    onPosted();
  };

  const submit = async (draft: AskDraft) => {
    if (submitting || !communityId) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await post(draft);
    } catch (thrown) {
      setError(serverMessage(thrown, failed));
    } finally {
      setSubmitting(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return { submit, submitting, error, clearError };
}
